import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, forkJoin } from 'rxjs';
import { EmployeeService } from 'src/app/services/employee.service';
import { UserProfile, EmergencyClient, Emergency, RegisteredSignal, RegisterSignalClient, EmergencyStatusClient, EmergencyStatus } from 'src/app/web-api-client';
import { WtStorageService } from 'src/app/wt-storage.service';
import { UserProfessionTypes } from '../../enums/user-professions.enum';
import { DataLayoutToggle } from '../../interfaces/data-layout-toggle.interface';
import { AddEmergencyComponent } from '../modals/add-emergency/add-emergency.component';
import { SignalStatusTypes } from '../../enums/signal-status.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { EmergencyFilter } from '../../models/emergency-filter.model';
import { EmergencyDetailsComponent } from '../modals/emergency-details/emergency-details.component';

@Component({
  selector: 'app-emergencies-list',
  templateUrl: './emergencies-list.component.html',
  styleUrls: ['./emergencies-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmergenciesListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();

  public isLoading = false;
  public userProfile: UserProfile;
  public userProfessionTypes = UserProfessionTypes;

  //Must be updated if the grid cols number is changed!
  public readonly gridColsNum = 8;

  public emergenciesList: Emergency[] = [];
  public filteredEmergenciesList: Emergency[] = [];

  public emergencyStatusList: EmergencyStatus[] = [];

  //The registered signals which are closed with identified issue!
  public closedRegisteredSignalsList: RegisteredSignal[] = [];

  public emergenciesMapView = new Map<number, Emergency>();

  //The registered signals which are closed with identified issue and unconverted to emergency!
  //These signals can be converted to emergencies!
  public unconvertedRegisteredSignalsList: RegisteredSignal[] = [];



  public viewToggleOptions: DataLayoutToggle[] = [
    { id: 'grid', value: 'Таблица' },
    { id: 'map', value: 'Карта' }
  ];
  public currentlySelectedView: DataLayoutToggle = this.viewToggleOptions[0];

  /** Indicator if the filters form is visible/hidden */
  public searchFiltersToggled = false;

  /** Current applied filters */
  public emergenciesSearchFilter: EmergencyFilter;

  constructor(
    private readonly fb: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly employeeService: EmployeeService,
    private readonly emergencyClientService: EmergencyClient,
    private readonly registerSignalService: RegisterSignalClient,
    private readonly emergencyStatusService: EmergencyStatusClient,
    private readonly localStorage: WtStorageService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router
  ) {
    console.log(this.router.getCurrentNavigation().extras.state);
  }

  ngOnInit(): void {
    this.userProfile = this.localStorage.getuser();

    this.loadData();
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public get isGridView(): boolean {
    return this.currentlySelectedView.id === 'grid';
  }

  public get isMapView(): boolean {
    return this.currentlySelectedView.id === 'map';
  }

  public get isUserRepairGroupManager(): boolean {
    return this.userProfile.userProfessionIdUserProfession === UserProfessionTypes.REPAIR_GROUP_MANAGER;
  }

  public getSignalCheckoutDateTime(signalId: number): Date {
    const signal = this.closedRegisteredSignalsList.find(s => s.id === signalId);
    return signal?.checkoutWhen;
  }

  public onViewChange($event: DataLayoutToggle) {
    this.currentlySelectedView = $event;
  }

  public onAddNewEmergencyClicked() {
    const modalRef = this.modalService.open(AddEmergencyComponent, { size: 'xl', centered: true });
    const componentInstance = <AddEmergencyComponent>modalRef.componentInstance;
    componentInstance.closedSignalsList = this.unconvertedRegisteredSignalsList;

    this.subscriptions.add(
      componentInstance.onEmergencyCreated.subscribe(registeredEmergency => {
        //this.initializeEmergencyFilterForm();
        this.loadData();
      })
    );
  }



  public onToggleSearchFiltersClicked() {
    this.searchFiltersToggled = !this.searchFiltersToggled;
    this.changeDetectorRef.detectChanges();
  }

  public onToggleMapView(emergency: Emergency) {
    if (this.isLocationMapView(emergency)) {
      this.emergenciesMapView.delete(emergency.id);
    } else {
      this.emergenciesMapView.set(emergency.id, emergency);
    }

    this.changeDetectorRef.detectChanges();
  }

  public isLocationMapView(emergency: Emergency) {
    return this.emergenciesMapView.has(emergency.id);
  }

  public onEmergencyEditClicked(emergency: Emergency) {
    // this.emergencyClientService.remove(emergency.id).subscribe((result) => {
    //   console.log("Removed emergency with id: " + emergency.id);
    // });

    const modalRef = this.modalService.open(AddEmergencyComponent, { size: 'xl', centered: true });
    const componentInstance = <AddEmergencyComponent>modalRef.componentInstance;
    componentInstance.emergency = emergency;
    componentInstance.closedSignalsList = this.closedRegisteredSignalsList;
  }

  public openReadOnlyEmergencyViewModal(emergency: Emergency) {
    const modalRef = this.modalService.open(EmergencyDetailsComponent, { size: 'xl', centered: true });
    const componentInstance = <EmergencyDetailsComponent>modalRef.componentInstance;
    componentInstance.emergency = emergency;
    componentInstance.signal = this.closedRegisteredSignalsList.find(s => s.id === emergency.registeredSignalId);
  }

  public onFilterSignalsList(searchFilter: EmergencyFilter) {
    this.emergenciesSearchFilter = searchFilter;

    console.log(searchFilter);
    this.filteredEmergenciesList = this.emergenciesList.filter(rs => {
      const startDateFlag = searchFilter.startDate
        ? rs.registeredSignal.when.getTime() >= searchFilter.startDate.getTime()
        : true;

      const endDateFlag = searchFilter.endDate
        ? rs.registeredSignal.when.getTime() <= searchFilter.endDate.getTime()
        : true;

      const statusFlag = searchFilter.emergencyStatusType
        ? rs.emergencyStatus.id === searchFilter.emergencyStatusType
        : true;

      return startDateFlag && endDateFlag && statusFlag;
    });

    this.changeDetectorRef.detectChanges();
  }

  public onClearFiltersClicked() {
    this.resetSignalsSearchFilter();
    this.changeDetectorRef.detectChanges();
  }

  private resetSignalsSearchFilter() {
    this.filteredEmergenciesList = this.emergenciesList;
    this.emergenciesSearchFilter = null;
  }

  public getStatus(item: Emergency): string {

    return item && item.emergencyStatus ? item.emergencyStatus.title : '';
  }

  public canEditEmergency(emergency:Emergency): boolean {

    let isInProperRole = this.userProfile.userProfession.idUserProfession == this.userProfessionTypes.REPAIR_GROUP_MANAGER;

    let result = isInProperRole && emergency.workingGroupsId == this.userProfile.idWorkingGroups;

    return result;
  }

  private loadData() {
    this.isLoading = true;

    forkJoin([
      this.emergencyClientService.getList(),
      this.registerSignalService.getList(),
      this.emergencyStatusService.getList()
    ]).subscribe(([emergenciesList, registeredSignalsList, emergencyStatusList]) => {
      this.closedRegisteredSignalsList = registeredSignalsList.filter(s => {
        return s.signalStatusId === SignalStatusTypes.CLOSED_ISSUE_IDENTIFIED;
      });

      this.unconvertedRegisteredSignalsList = this.closedRegisteredSignalsList.filter(s => {
        return !s.emergencyId;
      });
      this.emergenciesList = emergenciesList.slice();
      this.filteredEmergenciesList = this.emergenciesList;
      this.emergencyStatusList = emergencyStatusList;

      console.log("closedRegisteredSignalsList");
      console.log(this.unconvertedRegisteredSignalsList);
      console.log("emergenciesList");
      console.log(this.emergenciesList);
      console.log("emergencyStatusList");
      console.log(this.emergencyStatusList);

      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    })
  }
}
